import { Root, Content, Trigger } from '@radix-ui/react-popover'
import type { PopoverContentProps } from '@radix-ui/react-popover'
import classnames from 'classnames'
import Link from 'next/link'
import type React from 'react'
import { useCallback, useEffect } from 'react'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import Tag from '../tag'

import styles from './index.module.scss'

type ItemCommon = {
  label: string
  disabled?: boolean
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  isActive?: boolean
  description?: string
  tag?: string
}
type ItemButton = ItemCommon & {
  href?: never
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void
}

type ItemLink = ItemCommon & {
  href: string
  onClick?: never
}

type MenuItems = ItemLink | ItemButton

export type GroupItems = {
  group?: string
  items: MenuItems[]
}

type BaseProps = {
  trigger: React.ReactNode
  items: GroupItems[]
  scrollLock?: boolean
  onOpenChangeCallback?: (open: boolean) => void
}

export type MenuProps = BaseProps & Omit<PopoverContentProps, keyof BaseProps>

export const MenuDropdown = ({
  trigger,
  items,
  scrollLock,
  onOpenChangeCallback,
  ...rest
}: MenuProps) => {
  const onOpenChange = useCallback(
    (open: boolean) => {
      onOpenChangeCallback?.(open)
      if (open && scrollLock) {
        disableBodyScroll(document.body, { reserveScrollBarGap: true })
        return
      }
      enableBodyScroll(document.body)
    },
    [scrollLock, onOpenChangeCallback]
  )

  useEffect(() => {
    return () => {
      enableBodyScroll(document.body)
    }
  })

  const renderIcon = useCallback((icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>) => {
    const Icon = icon
    return <Icon className={styles['icon']} />
  }, [])

  const renderContent = useCallback(
    ({ icon, isActive, description, label, tag }: ItemButton | ItemLink) => (
      <>
        <span className={styles['label-container']}>
          <span className={styles['label-content']}>
            {icon && renderIcon(icon)}
            <span className={classnames(styles['item-text'], { [styles.highlight]: isActive })}>
              {label}
            </span>
          </span>
          {description && <span className={styles['description']}>{description}</span>}
        </span>
        {tag && <Tag label={tag} size="small" />}
      </>
    ),
    [renderIcon]
  )

  const renderButton = useCallback(
    (item: ItemButton) => (
      <button type="button" className={styles['item']} {...item}>
        {renderContent(item)}
      </button>
    ),
    [renderContent]
  )

  const renderLink = useCallback(
    (item: ItemLink) => (
      <Link
        className={classnames(styles['item'], {
          [styles['disabled']]: item.disabled,
        })}
        tabIndex={item.disabled ? -1 : 0}
        {...item}
      >
        {renderContent(item)}
      </Link>
    ),
    [renderContent]
  )

  return (
    <Root onOpenChange={onOpenChange}>
      <Trigger asChild>{trigger}</Trigger>
      {/* TODO: Remove the wrapper div once radix-ui fix z-index error
        when using this component below another of the same type.
        <MenuDropdown />
        <MenuDropdown />
      */}
      <div className={styles['content-wrapper']}>
        <Content {...rest}>
          <ul className={styles['container']}>
            {items.map(section => (
              <li
                key={`${section.group}-${JSON.stringify(section.items)}`}
                className={styles['group']}
              >
                <ul>
                  {section.group && <p className={styles['group-title']}> {section.group}</p>}
                  {section.items.map(({ label, onClick, icon, href, disabled, ...restProps }) => (
                    <li className={styles['items-container']} key={label}>
                      {onClick
                        ? renderButton({ label, onClick, icon, disabled, ...restProps })
                        : renderLink({ label, href, icon, disabled, ...restProps } as ItemLink)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Content>
      </div>
    </Root>
  )
}

export default MenuDropdown
